import PropTypes from 'prop-types'
import React from 'react'
import { connect } from 'react-redux'
import {
  getStationsByAuto,
  getStationsByIpDefault,
  getStationsByZip,
} from '../../actions/contentActions'
import focusable from '../../components/Focusable/Focusable'
import DeleteBtn from '../../components/Icons/DeleteBtn/DeleteBtn.js'
import LogoHeader from '../../components/LogoHeader/LogoHeader'
import Spinner from '../../components/Spinner/Spinner'
import StationCarousel from '../../components/StationCarousel/StationCarousel.js'
import NumericButton from '../../components/StdButton/StdButton'
import tts from '../../lib/TTS.js'
import { isKeyNumeric } from '../../../../shared/keys/keyEvents'
import {
  ComcastErrorType,
  triggerSentryError,
} from '../../../../shared/helpers.js'
import googleAnalytics4 from '../../lib/googleAnalytics.js'
import { withRouter } from '../../../../shared/components/WithRouter/WithRouter'

class ChangeStationScreen extends React.Component {
  constructor(props) {
    super(props)

    this.componentType = 'changeStation'
    this.numericIndex = 0
    this.numerics = []
    this.zipQuery = ''

    //0 to 9
    this.numericCollection = [
      '1',
      '2',
      '3',
      '4',
      '5',
      '6',
      '7',
      '8',
      '9',
      '0',
      'DeleteNumeric',
    ]

    this.state = {
      id: 'ChangeStationScreen',
      stations: [],
      zipQuery: this.zipQuery,
      spinnerVisible: true,
      inputEnabled: false,
      deleteFocus: false,
      hasStations: true,
      focusedComponent: 'NumericButton',
    }

    tts.pushTextToRead(
      'You can change your station on this page. Enter a 5 digit zip code to find your local stations.'
    )
  }

  /* Life Cycles */

  componentDidMount() {
    this.getInitialStations()

    /* window.ga('send', {
      hitType: 'event',
      eventCategory: 'Settings Dialog',
      eventAction: 'Change Local Station',
      eventLabel: this.props.callSign,
    }) */

    googleAnalytics4.sendPageViewEvent('Change Station')
  }

  componentDidUpdate(prevProps, prevState) {
    if (
      this.state.spinnerVisible !== prevState.spinnerVisible &&
      this.state.spinnerVisible === false
    ) {
      this.setNumericFocus()
      tts.addTextToRead(
        `Focused on ${this.numerics[this.numericIndex].props.label}`
      )
    }
  }

  /* API Data Requests */

  getInitialStations() {
    //Make first attempt at getting stations using the 'auto' API.
    getStationsByAuto()
      .then((autoLocaliationResponse) => {
        getStationsByZip(autoLocaliationResponse.zipcode)
          .then((stationsByZipResponse) => {
            this.setStationState(stationsByZipResponse)
          })
          .catch((err) => {
            const errorDescription = `Error ${
              err.status || '(unknown)'
            } on GA Change Station screen getting default stations by IP`

            triggerSentryError('getStationsByZip', 'ChangeStationScreen.js', {
              errorMessage: err.message ?? 'Undefined error message (default)',
              errorDescription,
              errorLocation:
                'ChangeStationScreen - getInitialStations - getStationsByZip',
              errorType: ComcastErrorType.network,
            })

            this.setState({
              spinnerVisible: false,
            })
          })
      })
      .catch((err) => {
        //If the auto API is invalid (non US, or an internal IP) use the hardcoded IP address found in configuration.js
        const errorDescription = `Error ${
          err.status ? err.status : '(unknown)'
        } on GA Change Station screen getting default stations by 'auto' API`

        triggerSentryError('getStationsByAuto', 'ChangeStationScreen.js', {
          errorMessage: err.message ?? 'Undefined error message (default)',
          errorDescription,
          errorLocation:
            'ChangeStationScreen - getInitialStations - getStationsByAuto',
          errorType: ComcastErrorType.network,
        })

        getStationsByIpDefault()
          .then((stationsByIpResponse) => {
            this.setStationState(stationsByIpResponse)
          })
          .catch((err) => {
            const errorDescription = `Error ${
              err.status ? err.status : '(unknown)'
            } on GA Change Station screen getting default stations by IP`

            triggerSentryError(
              'getStationsByIpDefault',
              'ChangeStationScreen.js',
              {
                errorMessage:
                  err.message ?? 'Undefined error message (default)',
                errorDescription,
                errorLocation:
                  'ChangeStationScreen - getInitialStations - getStationsByIpDefault',
                errorType: ComcastErrorType.network,
              }
            )

            this.setState({
              spinnerVisible: false,
            })
          })
      })
  }

  getStationsBySearch(zip) {
    // reset lookupFailed state, we're making a new call
    this.setState({ lookupFailed: false })
    getStationsByZip(zip)
      .then((stationsByZipResponse) => {
        this.setStationState(stationsByZipResponse)
      })
      .catch((error) => {
        if (error.status && error.status !== 404) {
          tts.addTextToRead(
            "We weren't able to look up your zip code at this time. Please try again later."
          )
          this.setState({
            lookupFailed: true,
            hasStations: false,
            spinnerVisible: false,
          })
        } else {
          tts.addTextToRead(
            'There are no stations available for your selected zip code.'
          )
          // this error doesn't get sent to sentry;
          // it's just what happens when no stations are found for a given ZIP
          this.setState({
            lookupFailed: false,
            hasStations: false,
            spinnerVisible: false,
          })
        }
      })
  }

  setStationState(stationResponse) {
    const mapped = stationResponse.stations.map((x) => {
      return {
        title: x.common_name,
        address: x.address,
        cid: x.pbs_id,
      }
    })
    this.setState({
      stations: mapped,
      spinnerVisible: false,
      hasStations: true,
    })
  }

  /* Screen Functionality */

  //filter componentType to determine if the button focused is a-z or 0-9
  isNumeric(buttonType) {
    let localButtonType = buttonType
    //if we have an alphanumeric button (Buttona .. Button9)
    if (localButtonType.substring(0, localButtonType.length - 1) === 'Button') {
      //test the last character in the button name for Alpha / Numeric
      const numeric = new RegExp(/^[0-9]+$/).test(localButtonType.slice(-1))

      //Assign a new componentType
      if (numeric) {
        localButtonType = 'NumericButton'
      }
    }

    //return the original OR modified buttonType
    return localButtonType
  }

  onKeyEvent(key) {
    let handled = false
    const focusedComponent = window.focusedComponent
    const componentType = this.isNumeric(focusedComponent.componentType)

    if (key === 'BACK') {
      tts.stopRead()
      this.props.router.navigate(-1)
      handled = true
    }

    //If it's a direct input
    if (isKeyNumeric(key)) {
      tts.addTextToRead(`Selected ${key}`)
      this.inputSearchChar(key)
      return true
    }

    if (componentType) {
      switch (componentType) {
        /* Numeric 0 to 9 Buttons */
        case 'NumericButton':
          if ('DOWN' === key) {
            if (this.state.hasStations) {
              this.setCarouselFocus()
            }
            handled = false
          } else if (key === 'LEFT') {
            if ('undefined' !== typeof this.numerics[this.numericIndex - 1]) {
              this.numericIndex--
              tts.stopRead()
              this.setNumericFocus()
              tts.addTextToRead(
                `Focused on ${this.numerics[this.numericIndex].props.label}`
              )
              handled = true
            } else {
              this.numericIndex = 9
              tts.stopRead()
              this.ButtonDeleteNumeric?.setFocus()
              handled = true
            }
          } else if (key === 'RIGHT') {
            if ('undefined' !== typeof this.numerics[this.numericIndex + 1]) {
              this.numericIndex++
              tts.stopRead()
              this.setNumericFocus()
              tts.addTextToRead(
                `Focused on ${this.numerics[this.numericIndex].props.label}`
              )
              handled = true
            } else {
              this.setNumericFocus()
              handled = true
            }
          } else if ('ENTER' === key) {
            this.inputSearchChar(focusedComponent.props.children)
            handled = true
          }
          break

        /* Numeric 'delete' Button */
        case 'ButtonDeleteNumeric':
          if ('DOWN' === key) {
            if (this.state.hasStations) {
              this.setCarouselFocus()
            }
            handled = false
          } else if ('LEFT' === key) {
            this.Button0?.setFocus()
            this.unfocusDelete()
            handled = true
          } else if ('RIGHT' === key) {
            this.Button1?.setFocus()
            this.unfocusDelete()
            this.numericIndex = 0
            handled = true
          } else if ('ENTER' === key) {
            this.inputSearchChar('delete')
            handled = true
          }

          break

        case 'stationCarouselItem':
          if ('UP' === key) {
            this.setNumericFocus()
            handled = true
          }
          break

        default:
          break
      }
    }

    return handled
  }

  setCarouselFocus() {
    if ('undefined' !== typeof this.state.stations) {
      this.stationsCarousel?.setFocus()
      this.setState({
        carouselFocused: true,
        buttonFocus: false,
        deleteFocus: false,
        focusedComponent: 'stationsCarousel',
        newOffset: false,
      })
    }
  }

  inputSearchChar(char) {
    if (char !== 'delete') {
      if (this.zipQuery.length === 5) {
        return
      }
      tts.addTextToRead('Selected ' + char)

      this.zipQuery += char

      this.setState({
        zipQuery: this.zipQuery,
        inputEnabled: true,
      })

      if (this.zipQuery.length >= 5) {
        //GET STATIONS
        tts.addTextToRead(
          `Searching for available stations for zip code: ${this.zipQuery
            .split('')
            .join(' , ')}`
        )
        this.getStationsBySearch(this.zipQuery)
      }
    } else {
      //remove the last char added
      this.zipQuery = this.zipQuery.substring(0, this.zipQuery.length - 1)

      if (this.zipQuery.length === 0) {
        this.setState({
          zipQuery: '',
          inputEnabled: false,
        })
      } else {
        this.setState({
          zipQuery: this.zipQuery,
          stations: [],
        })
      }
    }
  }

  setNumericFocus() {
    this.numerics[this.numericIndex]?.setFocus()
    const focusedButton = window.focusedComponent.componentType
    let deleteBtnFocus = false

    if (focusedButton === 'ButtonDeleteNumeric') {
      deleteBtnFocus = true
      this.setState({
        focusedComponent: focusedButton,
        deleteFocus: deleteBtnFocus,
      })
    }
  }

  setRefs(component, name) {
    const focusedComponent = this.state.focusedComponent
    if (null !== component) {
      this[name] = component
      if ('Button1' === name && 'Button1' === focusedComponent) {
        this.numericIndex = 0
        this[name]?.setFocus()
      }
    }
  }

  unfocusDelete() {
    this.setState({
      deleteFocus: false,
    })
  }

  setButtonLabels(label) {
    let btnName = `Button${label}`
    let btnTxt = ''
    let btnTTS = ''

    if (btnName === 'ButtonDeleteNumeric') {
      btnTxt = (
        <DeleteBtn fill="#fff" fillOpacity="0.6" width="44" height="31" />
      )
      btnTTS = 'Delete button'
    } else {
      btnTxt = label
      btnTTS = `${label}`
    }

    return {
      btnName,
      btnTxt,
      btnTTS,
    }
  }

  render() {
    const {
      stations,
      hasStations,
      zipQuery,
      spinnerVisible,
      inputEnabled,
      lookupFailed,
    } = this.state

    return !spinnerVisible ? (
      <div className="change-station-container">
        <LogoHeader />
        <div className="search-controls">
          <div className={`zip-input ${inputEnabled ? 'enabled' : ''}`}>
            <div>{zipQuery === '' ? 'Enter 5-Digit ZIP Code' : zipQuery}</div>
          </div>
          <div className="search-buttons">
            <div className="numeric-buttons" id="numericButtons">
              {/* 0 to 9, delete */}
              {this.numericCollection.map((r, i) => {
                let btnData = this.setButtonLabels(r)
                return (
                  <NumericButton
                    parent={this}
                    key={i}
                    index={i}
                    componentType={btnData.btnName}
                    label={btnData.btnTTS}
                    ref={(component) => {
                      if (component) {
                        this.numerics[i] = component
                        this.setRefs(component, btnData.btnName)
                      }
                    }}
                  >
                    {btnData.btnTxt}
                  </NumericButton>
                )
              })}
            </div>
          </div>
        </div>
        <div className="near-you">STATIONS NEAR YOU:</div>
        {hasStations && (
          <div className="carousel-container">
            <StationCarousel
              id={'changeStation'}
              parent={this}
              hideMetaData={true}
              collection={stations}
              componentType="carousel"
              ref={(component) => {
                this.setRefs(component, 'stationsCarousel')
              }}
            />
          </div>
        )}
        {!hasStations && !lookupFailed && (
          <p className="station-error">
            There are no stations available for your selected ZIP Code.
          </p>
        )}
        {!hasStations && lookupFailed && (
          <p className="station-error">
            We weren&#39;t able to look up your ZIP Code at this time. Please
            try again later.
          </p>
        )}
      </div>
    ) : (
      <Spinner visible={spinnerVisible} screen={true} />
    )
  }
}

ChangeStationScreen.propTypes = {
  callSign: PropTypes.string,
  router: PropTypes.shape({
    navigate: PropTypes.func,
  }),
}

const mapStateToProps = (state) => {
  return {
    callSign: state.station['call_sign'],
  }
}

export default connect(
  mapStateToProps,
  null,
  null
)(withRouter(focusable(ChangeStationScreen)))
